import {
  createContext,
  useContext,
  useId,
  useState,
  type ReactNode,
} from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  H3,
  Icon,
  Modal,
  Notification,
  P,
  Small,
  StatusDot,
  Text,
} from "orcs-design-system";

import GuardrailHistory from "src/inbox/requisition/GuardrailHistory";
import { guardrailHistory } from "src/inbox/requisition/guardrailHistoryRows";
import {
  formatClockHours,
  formatRelativeAgo,
  formatWaitingHours,
  liveElapsedHours,
  showPastSla,
} from "src/pages/WorkforceGuardrails/clockHours";
import OptionWritesBlockedPopover from "src/pages/Designs/OptionWritesBlockedPopover";
import type { RefusalReason } from "src/pages/WorkforceGuardrails/ruleShape";

import ExceptionReasonForm, {
  EXCEPTION_REASON_MODAL_TITLE,
} from "./ExceptionReasonForm";
import { ExceptionReasonAttribution } from "./exceptionReasonAttribution";
import { PersonNameLink } from "./PersonNameLink";
import GuardrailCheckList, {
  GuardrailCardHeader,
  GuardrailCheckNotice,
} from "./GuardrailCheckList";
import {
  checkTitle,
  realRuleName,
  useGuardrailRuleLabels,
  type RuleLabel,
} from "./guardrailRuleLabels";
import GuardrailDecisionForm from "./GuardrailDecisionForm";
import {
  attentionGuardrailChecks,
  chainMayOpen,
  currentStepOrder,
  displaySteps,
  evalCollapsedTitle,
  quietGroupLine,
  summaryGuardrailChecks,
} from "./requisitionGuardrailChainView";
import {
  useGuardrailChainActions,
  type ChainActions,
} from "./useGuardrailChainActions";

export { withCheckLabels } from "./guardrailRuleLabels";

export type GuardrailCategory = "COST" | "LOCATION" | "STRUCTURE" | "STRATEGIC";

export type GuardrailCheck = {
  ruleId: string;
  /** A WARN failure stays on the stamp. It does not change the decision. */
  effect?:
    | "BLOCK"
    | "WARN"
    | "ADD_APPROVAL_STEP"
    | "SKIP_APPROVAL_STEP"
    | "AUTO_APPROVE"
    | null;
  state: "PASS" | "FAIL" | "UNKNOWN";
  detail: string;
  /** Rule name from the monitor. Used when category is missing. */
  name?: string | null;
  description?: string | null;
  category?: GuardrailCategory | null;
  ownerRole?: string | null;
};

export type GuardrailDecision = "PASS" | "HARD_BLOCK";

export type ChainTemplate = "standard" | "exception" | "fast-track";

export type AddingRule = {
  ruleId: string;
  ruleName?: string | null;
};

export type ApprovalGroup = {
  approverGroupId: string;
  approverGroupName?: string | null;
  status?: string | null;
  personId?: string | null;
  personName?: string | null;
  actedAt?: string | null;
  addingRules?: AddingRule[] | null;
  /** Closed clock hours. Null when the clock has not started. */
  elapsedHours?: number | null;
  clockStartedAt?: string | null;
  /** Null when the group has no SLA. */
  slaHours?: number | null;
  pastSla?: boolean | null;
  canRespond?: boolean | null;
  canUndo?: boolean | null;
  /** Labels stored at decision time. Do not look these up again. */
  reasons?: Array<{ id: string; label: string }> | null;
  comment?: string | null;
  /** Dynamic group. Absent on an ordinary group. */
  resolver?: string | null;
  /** Person resolved when the requisition was raised. Absent when none. */
  assigneePersonId?: string | null;
  assigneePersonName?: string | null;
};

export type ApprovalStep = {
  order: number;
  kind: string;
  ruleId?: string | null;
  /** Reason step only. A group step keeps status on each group. */
  status?: string | null;
  reason?: string | null;
  personId?: string | null;
  personName?: string | null;
  actedAt?: string | null;
  /** Approver groups on a group step, in order. */
  groups?: ApprovalGroup[] | null;
};

export type GuardrailEvaluation = {
  decision: GuardrailDecision;
  checks: GuardrailCheck[];
  evaluatedAt?: string | null;
  trigger?: string | null;
  template?: ChainTemplate | string | null;
  steps?: ApprovalStep[] | null;
};

export type GuardrailDecisionLogRescind = {
  personId?: string | null;
  personName?: string | null;
  actedAt?: string | null;
};

export type GuardrailDecisionLogEntry = {
  decision: "APPROVED" | "REFUSED";
  approverGroupId: string;
  approverGroupName?: string | null;
  order: number;
  personId?: string | null;
  personName?: string | null;
  actedAt?: string | null;
  reasons?: Array<{ id: string; label: string }> | null;
  comment?: string | null;
  rescindedBy?: GuardrailDecisionLogRescind | null;
};

export type GuardrailResult = {
  id?: string | null;
  openedDate?: string | null;
  createdAt?: string | null;
  createdBy?: {
    aggregateId?: string | null;
    firstName?: string | null;
    surname?: string | null;
    preferredName?: string | null;
  } | null;
  /** Lifecycle state. May Open is hidden once this leaves draft. */
  state?: string | null;
  guardrailDecision?: GuardrailDecision | null;
  guardrailEvaluation?: GuardrailEvaluation | null;
  guardrailDecisionLog?: GuardrailDecisionLogEntry[] | null;
  lifecycleEvents?: Array<{
    from?: string | null;
    to?: string | null;
    at?: string | null;
  }> | null;
};

const isGuardrailDecision = (value: unknown): value is GuardrailDecision =>
  value === "PASS" || value === "HARD_BLOCK";

export type HeaderBadge = {
  label: string;
  variant: "success" | "warning" | "danger" | "primary";
};

const passBadge = (template: GuardrailEvaluation["template"]): HeaderBadge => {
  switch (template) {
    case "exception":
      return { label: "Exception", variant: "warning" };
    case "fast-track":
      return { label: "Fast-track", variant: "primary" };
    case "standard":
    case null:
    case undefined:
      return { label: "Pass", variant: "success" };
    default:
      return { label: "Pass", variant: "success" };
  }
};

const headerBadge = (
  decision: GuardrailDecision,
  template: GuardrailEvaluation["template"]
): HeaderBadge => {
  switch (decision) {
    case "HARD_BLOCK":
      return { label: "Hard block", variant: "danger" };
    case "PASS":
      return passBadge(template);
    default: {
      const unexpected: never = decision;
      return unexpected;
    }
  }
};

/** Registry tables and summaries — same labels as the outcome header badge. */
export const getGuardrailDecisionDisplay = (
  decision: GuardrailDecision | null | undefined,
  template?: GuardrailEvaluation["template"] | null
): HeaderBadge | null => {
  if (!isGuardrailDecision(decision)) {
    return null;
  }
  return headerBadge(decision, template ?? null);
};

const statusLabel = (status: string | null | undefined): string => {
  switch (status) {
    case "PENDING":
      return "Pending";
    case "APPROVED":
      return "Approved";
    case "REFUSED":
      return "Refused";
    default:
      return "";
  }
};

const EXCEPTION_REASON_CTA = "Add exception reason";

const MAY_OPEN_NOTICE =
  "May Open — the chain is complete. Guardrails does not set an Approved status.";

const groupsOrEmpty = (groups: ApprovalGroup[] | null | undefined) =>
  groups ?? [];

const stepGroupsOf = (step: ApprovalStep): ApprovalGroup[] => {
  if (step.kind === "group") return groupsOrEmpty(step.groups);
  return [];
};

const groupIsPast = (nowMs: number) => (group: ApprovalGroup) =>
  showPastSla({
    slaHours: group.slaHours,
    pastSla: group.pastSla === true,
    elapsedHours: group.elapsedHours,
    clockStartedAt: group.clockStartedAt,
    nowMs,
  });

const storedDecisionParts = (group: ApprovalGroup): string[] => {
  const labels = (group.reasons ?? []).flatMap((reason) =>
    reason.label.trim() === "" ? [] : [reason.label]
  );
  const comment = group.comment ?? "";
  if (comment.trim() === "") return labels;
  return [...labels, comment];
};

const withStoredDecision = (line: string, group: ApprovalGroup) => {
  const parts = storedDecisionParts(group);
  if (parts.length === 0) return line;
  return `${line}. ${parts.join(". ")}`;
};

const withStoredDecisionNode = (
  line: ReactNode,
  group: ApprovalGroup
): ReactNode => {
  const parts = storedDecisionParts(group);
  if (parts.length === 0) return line;
  return (
    <>
      {line}. {parts.join(". ")}
    </>
  );
};

const groupStatusLine = (
  group: ApprovalGroup,
  nowMs: number,
  withName = true
): ReactNode => {
  const name = groupNameOf(group);
  const status = statusLabel(group.status);
  if (status === "Approved" || status === "Refused") {
    const who = group.personName?.trim() || "Unknown";
    const rel =
      typeof group.actedAt === "string"
        ? formatRelativeAgo(group.actedAt, nowMs)
        : null;
    const whoNode = <PersonNameLink name={who} personId={group.personId} />;
    const head = withName ? `${name}: ${status}` : status;
    const line = rel ? (
      <>
        {head} by {whoNode} {rel}
      </>
    ) : (
      <>
        {head} by {whoNode}
      </>
    );
    return withStoredDecisionNode(line, group);
  }
  if (status) {
    const head = withName ? `${name}: ${status}` : status;
    const line = withStoredDecision(head, group);
    if (group.resolver && !group.assigneePersonId?.trim()) {
      return `${line}. No person.`;
    }
    if (status === "Pending" && group.assigneePersonId) {
      const who = group.assigneePersonName?.trim() || group.assigneePersonId;
      return (
        <>
          {line}.{" "}
          <PersonNameLink name={who} personId={group.assigneePersonId} />
        </>
      );
    }
    return line;
  }
  return withStoredDecision(name, group);
};

const undoLabel = (group: ApprovalGroup) => {
  if (group.status === "REFUSED") return "Rescind Refusal";
  return "Rescind Approval";
};

const trimmedName = (group: ApprovalGroup) => group.approverGroupName?.trim();

const groupNameOf = (group: ApprovalGroup) =>
  trimmedName(group) || "Approver group";

const reasonValue = (step: ApprovalStep) => step.reason ?? "";

const isPending = (step: ApprovalStep) => step.status === "PENDING";

const isWaitingStatus = (group: ApprovalGroup) =>
  group.status === "PENDING" || group.status == null || group.status === "";

/** "Waiting 3 days (SLA 1 h)". Coarse on purpose: the pill says overdue. */
const clockLine = (group: ApprovalGroup, nowMs: number): string | null => {
  const hours = liveElapsedHours(
    group.elapsedHours,
    group.clockStartedAt,
    nowMs
  );
  const sla =
    typeof group.slaHours === "number"
      ? formatClockHours(group.slaHours)
      : null;
  if (hours == null && sla == null) {
    return null;
  }
  if (hours == null) {
    return `SLA ${sla}`;
  }
  const head = `${isWaitingStatus(group) ? "Waiting" : "Took"} ${formatWaitingHours(hours)}`;
  return sla == null ? head : `${head} (SLA ${sla})`;
};

const UndoButton = ({
  group,
  order,
  actions,
}: {
  group: ApprovalGroup;
  order: number;
  actions: ChainActions;
}) => {
  if (group.canUndo) {
    return (
      <OptionWritesBlockedPopover blocked={actions.writesBlocked}>
        <Flex>
          <Button
            small
            type="button"
            variant="ghost"
            disabled={actions.saving || actions.writesBlocked}
            onClick={() => actions.undo(order, group.approverGroupId)}
          >
            {undoLabel(group)}
          </Button>
        </Flex>
      </OptionWritesBlockedPopover>
    );
  }
  return null;
};

const GroupActions = ({
  group,
  order,
  actions,
}: {
  group: ApprovalGroup;
  order: number;
  actions: ChainActions;
}) => (
  <>
    <GuardrailDecisionForm
      group={group}
      order={order}
      actions={actions}
      layout="inline"
    />
    <UndoButton group={group} order={order} actions={actions} />
  </>
);

const RuleCatalogueContext = createContext<{
  rules: RuleLabel[];
  loaded: boolean;
}>({ rules: [], loaded: false });

const AddingRulesLine = ({ group }: { group: ApprovalGroup }) => {
  const catalogue = useContext(RuleCatalogueContext);
  const rules = group.addingRules ?? [];
  if (rules.length === 0) return null;
  return (
    <>
      {rules.map((rule) => (
        <Small key={rule.ruleId} color="subText">
          Added by rule:{" "}
          {checkTitle(
            { name: rule.ruleName, ruleId: rule.ruleId },
            catalogue.rules,
            catalogue.loaded
          )}
        </Small>
      ))}
    </>
  );
};

const OVERDUE = "Overdue";

/** One group's lines. The step owns the name unless `showName`. */
const GroupBody = ({
  group,
  order,
  actions,
  showName,
  overduePill,
  inStepCard = false,
}: {
  group: ApprovalGroup;
  order: number;
  actions: ChainActions;
  showName: boolean;
  overduePill: boolean;
  /** The step card heading already names the group. */
  inStepCard?: boolean;
}) => {
  const line = clockLine(group, actions.nowMs);
  return (
    <Flex
      flexDirection="column"
      gap="xs"
      data-testid={`guardrail-approval-group-${order}-${group.approverGroupId}`}
    >
      {showName || overduePill ? (
        <Flex alignItems="flex-start" gap="s">
          {showName ? (
            <P fontSize="1" m="0" weight="bold">
              {groupNameOf(group)}
            </P>
          ) : null}
          {overduePill ? (
            <Badge variant="danger" ml="auto">
              {OVERDUE}
            </Badge>
          ) : null}
        </Flex>
      ) : null}
      {group.status === "PENDING" &&
      !group.resolver &&
      !group.assigneePersonId?.trim() ? null : (
        <P fontSize="1" m="0">
          {groupStatusLine(group, actions.nowMs, !(showName || inStepCard))}
        </P>
      )}
      {line ? <Small color="subText">{line}</Small> : null}
      <AddingRulesLine group={group} />
      <GroupActions group={group} order={order} actions={actions} />
    </Flex>
  );
};

const ReasonPendingCta = ({ actions }: { actions: ChainActions }) => (
  <OptionWritesBlockedPopover blocked={actions.writesBlocked}>
    <Button
      variant="ghost"
      small
      type="button"
      disabled={actions.writesBlocked}
      onClick={actions.openReasonModal}
    >
      {EXCEPTION_REASON_CTA}
    </Button>
  </OptionWritesBlockedPopover>
);

const ExceptionReasonModal = ({
  visible,
  step,
  draft,
  saving,
  error,
  onChange,
  onClose,
  onSave,
}: {
  visible: boolean;
  step: ApprovalStep;
  draft: string;
  saving: boolean;
  error: string | null;
  onChange: (value: string) => void;
  onClose: () => void;
  onSave: (reason: string) => void;
}) => {
  if (!visible) return null;
  return (
    <Modal
      visible
      onClose={onClose}
      ariaLabel={EXCEPTION_REASON_MODAL_TITLE}
      width="640px"
      maxWidth="95vw"
      maxHeight="90vh"
      headerContent={<H3>{EXCEPTION_REASON_MODAL_TITLE}</H3>}
    >
      <Box data-testid="guardrail-exception-reason-modal">
        <ExceptionReasonForm
          layout="modal"
          draft={draft}
          saving={saving}
          error={error}
          fieldId={`guardrail-exception-reason-${step.order}`}
          onChange={onChange}
          onSave={onSave}
          onCancel={onClose}
        />
      </Box>
    </Modal>
  );
};

const SavedReason = ({
  step,
  nowMs,
}: {
  step: ApprovalStep;
  nowMs: number;
}) => (
  <Flex flexDirection="column" gap="xs">
    <P fontSize="1" m="0">
      {reasonValue(step)}
    </P>
    <ExceptionReasonAttribution
      personName={step.personName}
      personId={step.personId}
      stamp={
        typeof step.actedAt === "string"
          ? formatRelativeAgo(step.actedAt, nowMs)
          : null
      }
    />
  </Flex>
);

const ReasonBody = ({
  step,
  actions,
}: {
  step: ApprovalStep;
  actions: ChainActions;
}) => {
  if (isPending(step)) {
    return (
      <Flex flexDirection="column" gap="xs" alignItems="flex-start">
        <P fontSize="1" m="0" weight="bold">
          Exception reason
        </P>
        {actions.readOnly ? null : <ReasonPendingCta actions={actions} />}
      </Flex>
    );
  }
  return (
    <Flex flexDirection="column" gap="xs" alignItems="flex-start">
      <P fontSize="1" m="0" weight="bold">
        Exception requested
      </P>
      <SavedReason step={step} nowMs={actions.nowMs} />
    </Flex>
  );
};

const mustApprove = (count: number) =>
  count === 2
    ? "Both groups must approve before this step completes."
    : "All groups must approve before this step completes.";

const onceApproved = (count: number) => {
  if (count === 1) {
    return "this group approves";
  }
  return count === 2 ? "both groups approve" : "all groups approve";
};

const groupIsActionable = (group: ApprovalGroup) =>
  group.canRespond === true || group.canUndo === true;

const QuietGroup = ({ group }: { group: ApprovalGroup }) => {
  const parts = storedDecisionParts(group);
  return (
    <Flex flexDirection="column" gap="xs">
      <Small color="subText">{quietGroupLine(group)}</Small>
      {parts.length > 0 ? (
        <Small color="subText">{parts.join(". ")}</Small>
      ) : null}
    </Flex>
  );
};

const joinNames = (names: string[]) => {
  if (names.length <= 1) {
    return names[0] ?? "";
  }
  const head = names.slice(0, -1).join(", ");
  return `${head} and ${names[names.length - 1]}`;
};

const stepNames = (step: ApprovalStep): string | null => {
  if (step.kind === "reason") {
    return "Exception reason";
  }
  const names = stepGroupsOf(step).map(groupNameOf);
  if (names.length === 0) {
    return null;
  }
  return joinNames(names);
};

/** The live gate. One group sits flat; several get a card each. */
const GroupStepCard = ({
  step,
  next,
  actions,
  showNext,
}: {
  step: ApprovalStep;
  next: ApprovalStep | undefined;
  actions: ChainActions;
  showNext: boolean;
}) => {
  const groups = stepGroupsOf(step);
  const isPast = groupIsPast(actions.nowMs);
  const past = groups.filter(isPast).length;
  const multi = groups.length > 1;
  const allPast = past === groups.length;
  const nextName = next == null ? null : stepNames(next);
  return (
    <Flex flexDirection="column" gap="s">
      <Flex justifyContent="space-between" alignItems="flex-start" gap="s">
        <P fontSize="1" m="0" weight="bold">
          {joinNames(groups.map(groupNameOf))}
        </P>
        {past > 0 ? (
          <Badge variant="danger">{multi ? `${past} overdue` : OVERDUE}</Badge>
        ) : null}
      </Flex>
      {multi ? (
        <Small color="subText">{mustApprove(groups.length)}</Small>
      ) : null}
      {multi
        ? groups.map((group) => (
            <Box
              key={group.approverGroupId}
              boxBorder="default"
              borderRadius="2"
              p="s"
              bg="surface"
            >
              <GroupBody
                group={group}
                order={step.order}
                actions={actions}
                showName
                overduePill={!allPast && isPast(group)}
              />
            </Box>
          ))
        : groups.map((group) => (
            <GroupBody
              key={group.approverGroupId}
              group={group}
              order={step.order}
              actions={actions}
              showName={false}
              overduePill={false}
              inStepCard
            />
          ))}
      {showNext && next != null && nextName != null ? (
        <Small color="subText">
          {nextName} starts once {onceApproved(groups.length)}.
        </Small>
      ) : null}
    </Flex>
  );
};

const WaitingStep = ({ step }: { step: ApprovalStep }) => {
  const groups = stepGroupsOf(step);
  const slas = [
    ...new Set(
      groups.flatMap((group) =>
        typeof group.slaHours === "number" ? [group.slaHours] : []
      )
    ),
  ];
  const sla = slas.length === 1 ? ` · SLA ${formatClockHours(slas[0])}` : "";
  const count = groups.length === 1 ? "1 group" : `${groups.length} groups`;
  return (
    <Flex justifyContent="space-between" alignItems="flex-start" gap="s">
      <Flex flexDirection="column" gap="xs" minWidth="0">
        <P fontSize="1" m="0" weight="bold" color="subText">
          {joinNames(groups.map(groupNameOf))}
        </P>
        <Small color="subText">
          {count}
          {sla}
        </Small>
      </Flex>
      <Small color="subText">Not started</Small>
    </Flex>
  );
};

type ChainTone = "done" | "current" | "refused" | "waiting";

const chainTone = (step: ApprovalStep, current: boolean): ChainTone => {
  if (step.kind === "reason") {
    return step.status === "PENDING" ? "current" : "done";
  }
  const groups = stepGroupsOf(step);
  if (groups.some((group) => group.status === "REFUSED")) {
    return "refused";
  }
  if (
    groups.length > 0 &&
    groups.every((group) => group.status === "APPROVED")
  ) {
    return "done";
  }
  if (current) {
    return "current";
  }
  return "waiting";
};

type Marker = ChainTone | "warning" | "noted";

const markerOf = (step: ApprovalStep, tone: ChainTone): Marker => {
  if (step.kind === "reason" && tone === "done") {
    return "noted";
  }
  return tone;
};

const TimelineMarker = ({ marker }: { marker: Marker }) => {
  switch (marker) {
    case "done":
      return (
        <StatusDot variant="success" icon>
          <Icon icon="check" color="white" size="xs" />
        </StatusDot>
      );
    case "current":
      return (
        <StatusDot variant="default" icon>
          <Icon icon="clock" color="white" size="xs" />
        </StatusDot>
      );
    case "refused":
      return (
        <StatusDot variant="danger" icon>
          <Icon icon={["fas", "exclamation"]} color="white" size="xs" />
        </StatusDot>
      );
    case "warning":
      return (
        <StatusDot variant="warning" icon>
          <Icon icon={["fas", "exclamation"]} color="white" size="xs" />
        </StatusDot>
      );
    case "noted":
      return (
        <StatusDot variant="disabled" icon>
          <Icon icon="comment" color="greyDarkest" size="xs" />
        </StatusDot>
      );
    case "waiting":
      return <StatusDot variant="disabled" icon />;
    default: {
      const unexpected: never = marker;
      return unexpected;
    }
  }
};

const TimelineRow = ({
  marker,
  urgent = false,
  last,
  framed,
  testId,
  children,
}: {
  marker: Marker;
  urgent?: boolean;
  last: boolean;
  framed: boolean;
  testId?: string;
  children: ReactNode;
}) => (
  <Flex gap="s" alignItems="stretch" data-testid={testId}>
    <Flex flexDirection="column" alignItems="center" flexShrink={0}>
      <TimelineMarker marker={marker} />
      {last ? null : (
        <Box width="1px" bg="surfaceBorder" flex="1" minHeight="s" />
      )}
    </Flex>
    <Box flex="1" minWidth="0" pb={last ? "0" : "r"}>
      {framed ? (
        <Notification
          variant={urgent ? "error" : "informational"}
          closable={false}
          noIcon
          noAccent
          width="100%"
        >
          {children}
        </Notification>
      ) : (
        children
      )}
    </Box>
  </Flex>
);

const HumanBody = ({
  step,
  next,
  current,
  actions,
}: {
  step: ApprovalStep;
  next: ApprovalStep | undefined;
  current: boolean;
  actions: ChainActions;
}) => {
  if (step.kind === "reason") {
    return <ReasonBody step={step} actions={actions} />;
  }
  const groups = stepGroupsOf(step);
  const tone = chainTone(step, current);
  const actionable = groups.some(groupIsActionable);
  if (tone === "waiting" && !actionable) {
    return <WaitingStep step={step} />;
  }
  if (tone === "current" || tone === "refused") {
    return (
      <GroupStepCard
        step={step}
        next={next}
        actions={actions}
        showNext={tone !== "refused"}
      />
    );
  }
  return (
    <Flex flexDirection="column" gap="xs" alignItems="stretch">
      {groups.map((group) =>
        groupIsActionable(group) ? (
          <GroupBody
            key={group.approverGroupId}
            group={group}
            order={step.order}
            actions={actions}
            showName={false}
            overduePill={false}
          />
        ) : (
          <QuietGroup key={group.approverGroupId} group={group} />
        )
      )}
    </Flex>
  );
};

const stepIsUrgent = (step: ApprovalStep, tone: ChainTone, nowMs: number) => {
  if (tone === "refused") {
    return true;
  }
  if (tone !== "current") {
    return false;
  }
  return stepGroupsOf(step).some(groupIsPast(nowMs));
};

const pendingNames = (step: ApprovalStep): string[] =>
  stepGroupsOf(step).flatMap((group) =>
    group.status === "APPROVED" ? [] : [groupNameOf(group)]
  );

const chainHeadline = ({
  decision,
  template,
  steps,
  currentOrder,
}: {
  decision: GuardrailDecision;
  template: GuardrailEvaluation["template"];
  steps: readonly ApprovalStep[];
  currentOrder: number | null;
}): string | null => {
  if (decision === "HARD_BLOCK" || currentOrder == null) {
    return null;
  }
  const step = steps.find((item) => item.order === currentOrder);
  if (step == null || step.kind !== "group") {
    return null;
  }
  const refused = stepGroupsOf(step).find(
    (group) => group.status === "REFUSED"
  );
  if (refused) {
    return `${groupNameOf(refused)} refused`;
  }
  const names = pendingNames(step);
  if (names.length === 0) {
    return null;
  }
  const who = joinNames(names);
  if (template === "exception") {
    return `Exception awaiting ${who}`;
  }
  if (template === "fast-track") {
    return `Fast-track awaiting ${who}`;
  }
  return `Awaiting ${who}`;
};

/** Counts the nodes on screen: the evaluation plus every human step. */
const chainProgress = (
  humanSteps: readonly ApprovalStep[],
  currentOrder: number | null
): string => {
  const tones = humanSteps.map((step) =>
    chainTone(step, currentOrder === step.order)
  );
  if (tones.some((tone) => tone === "refused")) {
    return "Refused";
  }
  const done = 1 + tones.filter((tone) => tone === "done").length;
  const total = 1 + humanSteps.length;
  if (done === total) {
    return "Complete";
  }
  return `${done} of ${total} done`;
};

/** The chip answers "can this open?", not "did the rules run?". */
const panelBadge = ({
  decision,
  template,
  humanSteps,
  currentOrder,
  nowMs,
}: {
  decision: GuardrailDecision;
  template: GuardrailEvaluation["template"];
  humanSteps: readonly ApprovalStep[];
  currentOrder: number | null;
  nowMs: number;
}): HeaderBadge => {
  if (decision === "HARD_BLOCK" || humanSteps.length === 0) {
    return headerBadge(decision, template);
  }
  const current = humanSteps.find((step) => step.order === currentOrder);
  if (current == null) {
    return { label: "Complete", variant: "success" };
  }
  if (current.kind === "reason") {
    return { label: "Reason needed", variant: "warning" };
  }
  const groups = stepGroupsOf(current);
  if (groups.some((group) => group.status === "REFUSED")) {
    return { label: "Refused", variant: "danger" };
  }
  if (groups.some(groupIsPast(nowMs))) {
    return { label: OVERDUE, variant: "danger" };
  }
  return { label: "Awaiting approval", variant: "primary" };
};

const rulesCheckedLine = (failed: number, passed: number): string | null => {
  const total = failed + passed;
  if (total === 0) {
    return null;
  }
  const checked = total === 1 ? "1 rule checked" : `${total} rules checked`;
  if (failed === 0) {
    return checked;
  }
  const fail = failed === 1 ? "1 failed" : `${failed} failed`;
  return `${checked} · ${fail}`;
};

const passedRulesTitle = (passed: number) =>
  passed === 1 ? "1 other rule passed" : `${passed} other rules passed`;

/** Text-size disclosure. The body stays in the DOM, hidden, like Expandable. */
const Disclosure = ({
  title,
  defaultOpen,
  children,
}: {
  title: string;
  defaultOpen: boolean;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const id = useId();
  return (
    <Flex flexDirection="column" gap="xs" alignItems="flex-start">
      <Button
        variant="subtle"
        small
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon
          icon={open ? "chevron-down" : "chevron-right"}
          size="xs"
          mr="xs"
          color="subText"
        />
        <Text color="subText">{title}</Text>
      </Button>
      <Box id={id} display={open ? "block" : "none"} width="100%">
        {children}
      </Box>
    </Flex>
  );
};

const EvalBlock = ({
  decision,
  checks,
  evaluated,
  testId,
}: {
  decision: GuardrailDecision;
  checks: GuardrailCheck[];
  evaluated: string | null;
  testId: string;
}) => {
  const catalogue = useContext(RuleCatalogueContext);
  const attention = attentionGuardrailChecks(checks);
  const summary = summaryGuardrailChecks(checks);
  const showPasses = summary.length > 0 || attention.length === 0;
  const checked = rulesCheckedLine(attention.length, summary.length);
  const stamp = evaluated ? <Small color="subText">{evaluated}</Small> : null;
  const passes = showPasses ? (
    <Disclosure
      title={
        attention.length > 0
          ? passedRulesTitle(summary.length)
          : evalCollapsedTitle(checks)
      }
      defaultOpen={decision === "HARD_BLOCK" && attention.length === 0}
    >
      <GuardrailCheckList checks={summary} />
    </Disclosure>
  ) : null;
  if (attention.length === 0) {
    return (
      <Flex
        justifyContent="space-between"
        alignItems="flex-start"
        gap="s"
        data-testid={testId}
      >
        <Box flex="1" minWidth="0">
          {passes}
        </Box>
        {stamp}
      </Flex>
    );
  }
  return (
    <Flex flexDirection="column" gap="s" data-testid={testId}>
      <Flex justifyContent="space-between" alignItems="flex-start" gap="s">
        <Flex flexDirection="column" gap="xs" minWidth="0">
          <P fontSize="1" m="0" weight="bold">
            Automatic evaluation
          </P>
          {checked ? <Small color="subText">{checked}</Small> : null}
        </Flex>
        {stamp}
      </Flex>
      {attention.map((check) => (
        <GuardrailCheckNotice
          key={check.ruleId}
          check={check}
          rules={catalogue.rules}
          loaded={catalogue.loaded}
        />
      ))}
      {passes}
    </Flex>
  );
};

const ErrorLine = ({ actionError }: { actionError: string | null }) => {
  if (actionError) {
    return (
      <P fontSize="1" m="0">
        {actionError}
      </P>
    );
  }
  return null;
};

type ChainLayout = "card" | "section";

export type GuardrailChain = ReturnType<typeof useGuardrailChainActions>;

const chainShape = (
  decision: GuardrailDecision,
  stampSteps: ApprovalStep[] | null | undefined
) => {
  const steps = displaySteps(decision, stampSteps);
  return {
    steps,
    currentOrder: currentStepOrder(decision, steps),
    humanSteps: steps.filter((step) => step.kind !== "evaluation"),
  };
};

/** Requisition header chip. Null once there is nothing left to wait on. */
export const requisitionChainBadge = (
  chain: GuardrailChain
): HeaderBadge | null => {
  const { decision } = chain;
  if (decision == null) {
    return null;
  }
  if (decision === "HARD_BLOCK") {
    return headerBadge(decision, null);
  }
  const evaluation = chain.shown?.guardrailEvaluation;
  const { humanSteps, currentOrder } = chainShape(decision, evaluation?.steps);
  if (humanSteps.length === 0 || currentOrder == null) {
    return null;
  }
  return panelBadge({
    decision,
    template: evaluation?.template,
    humanSteps,
    currentOrder,
    nowMs: chain.actions.nowMs,
  });
};

/** Section chip: the route taken, or Complete. The requisition owns the wait. */
const sectionBadge = (
  decision: GuardrailDecision,
  template: GuardrailEvaluation["template"],
  humanSteps: readonly ApprovalStep[],
  currentOrder: number | null
): HeaderBadge | null => {
  if (decision === "HARD_BLOCK") {
    return null;
  }
  if (currentOrder == null) {
    return humanSteps.length > 0
      ? { label: "Complete", variant: "success" }
      : passBadge(template);
  }
  if (template === "exception" || template === "fast-track") {
    return passBadge(template);
  }
  return null;
};

const countOf = (count: number, one: string, many: string) =>
  count === 1 ? `1 ${one}` : `${count} ${many}`;

/** "7 rules passed · 2 approvals · last by Madison Singh, 3 days ago". */
const chainSummary = (
  checks: readonly GuardrailCheck[],
  humanSteps: readonly ApprovalStep[],
  nowMs: number
): string => {
  const passed = summaryGuardrailChecks(checks).length;
  const approved = humanSteps
    .flatMap(stepGroupsOf)
    .filter((group) => group.status === "APPROVED");
  const parts: string[] = [];
  if (passed > 0) {
    parts.push(countOf(passed, "rule passed", "rules passed"));
  }
  if (approved.length > 0) {
    parts.push(countOf(approved.length, "approval", "approvals"));
  }
  const last = approved
    .filter(
      (group) =>
        typeof group.actedAt === "string" && Boolean(group.personName?.trim())
    )
    .sort((left, right) =>
      String(right.actedAt).localeCompare(String(left.actedAt))
    )[0];
  if (last != null && typeof last.actedAt === "string") {
    parts.push(
      `last by ${last.personName?.trim()}, ${formatRelativeAgo(last.actedAt, nowMs)}`
    );
  }
  return parts.join(" · ");
};

const ChainTimeline = ({
  humanSteps,
  currentOrder,
  evaluation,
  evalMarker,
  actions,
  header,
}: {
  humanSteps: readonly ApprovalStep[];
  currentOrder: number | null;
  evaluation: ReactNode;
  evalMarker: Marker;
  actions: ChainActions;
  header: ReactNode;
}) => (
  <Flex
    flexDirection="column"
    data-testid="guardrail-approval-steps"
    data-layout="vertical"
  >
    {header}
    <TimelineRow marker={evalMarker} last={false} framed={false}>
      {evaluation}
    </TimelineRow>
    {humanSteps.map((step, index) => {
      const current = currentOrder === step.order;
      const tone = chainTone(step, current);
      const urgent = stepIsUrgent(step, tone, actions.nowMs);
      return (
        <TimelineRow
          key={`${step.kind}-${step.order}`}
          marker={markerOf(step, tone)}
          urgent={urgent}
          last={index === humanSteps.length - 1}
          framed={tone === "current" || tone === "refused"}
          testId={`guardrail-approval-step-${step.order}`}
        >
          <HumanBody
            step={step}
            next={humanSteps[index + 1]}
            current={current}
            actions={actions}
          />
        </TimelineRow>
      );
    })}
  </Flex>
);

const ShowChainButton = ({
  open,
  onToggle,
}: {
  open: boolean;
  onToggle: () => void;
}) => (
  <Button
    variant="subtle"
    small
    type="button"
    aria-expanded={open}
    onClick={onToggle}
  >
    {open ? "Hide chain" : "Show chain"}
    <Icon icon={open ? "chevron-up" : "chevron-down"} size="xs" />
  </Button>
);

const OutcomeCard = ({
  layout,
  announcementRole,
  decision,
  checks,
  stampSteps,
  state,
  template,
  evaluatedAt,
  actions,
  actionError,
  historyRows,
}: {
  layout: ChainLayout;
  announcementRole: "alert" | "status";
  decision: GuardrailDecision;
  checks: GuardrailCheck[];
  /** Stored stamp. Not the hook's write steps. */
  stampSteps: ApprovalStep[] | null | undefined;
  state?: string | null | undefined;
  template: GuardrailEvaluation["template"];
  evaluatedAt?: string | null | undefined;
  actions: ChainActions;
  actionError: string | null;
  historyRows: ReturnType<typeof guardrailHistory>;
}) => {
  const { steps, currentOrder, humanSteps } = chainShape(decision, stampSteps);
  const attention = attentionGuardrailChecks(checks);
  const collapsible =
    layout === "section" &&
    decision === "PASS" &&
    currentOrder == null &&
    attention.length === 0;
  const [chainOpen, setChainOpen] = useState(!collapsible);
  const collapsed = collapsible && !chainOpen;
  const mayOpen = chainMayOpen({
    decision,
    steps,
    currentOrder,
    state,
  });
  const evalOrder =
    steps.find((step) => step.kind === "evaluation")?.order ?? 0;
  const evaluated =
    typeof evaluatedAt === "string"
      ? formatRelativeAgo(evaluatedAt, actions.nowMs)
      : null;
  const evaluation = (
    <EvalBlock
      decision={decision}
      checks={checks}
      evaluated={evaluated}
      testId={`guardrail-approval-step-${evalOrder}`}
    />
  );
  const evalMarker: Marker = attention.length > 0 ? "warning" : "done";
  const progress =
    humanSteps.length > 0 ? chainProgress(humanSteps, currentOrder) : null;
  const tail = (
    <>
      {mayOpen ? (
        <Notification variant="success" closable={false}>
          {MAY_OPEN_NOTICE}
        </Notification>
      ) : null}
      <ErrorLine actionError={actionError} />
    </>
  );

  if (layout === "section") {
    const badge = sectionBadge(decision, template, humanSteps, currentOrder);
    return (
      <Flex
        flexDirection="column"
        gap="s"
        role={announcementRole}
        data-testid="requisition-guardrail-outcome"
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          gap="s"
          flexWrap="wrap"
        >
          <Flex alignItems="center" gap="s" flexWrap="wrap" minWidth="0">
            <Small uppercase weight="bold" color="subText">
              Guardrails
            </Small>
            {badge ? (
              <Badge variant={badge.variant}>{badge.label}</Badge>
            ) : null}
            {collapsed ? (
              <Small color="subText">
                {chainSummary(checks, humanSteps, actions.nowMs)}
              </Small>
            ) : null}
          </Flex>
          <Flex alignItems="center" gap="s">
            {progress && !collapsed ? (
              <Small color="subText">{progress}</Small>
            ) : null}
            {collapsible ? (
              <ShowChainButton
                open={chainOpen}
                onToggle={() => setChainOpen((open) => !open)}
              />
            ) : null}
          </Flex>
        </Flex>
        {collapsed ? null : humanSteps.length > 0 ? (
          <ChainTimeline
            humanSteps={humanSteps}
            currentOrder={currentOrder}
            evaluation={evaluation}
            evalMarker={evalMarker}
            actions={actions}
            header={null}
          />
        ) : (
          evaluation
        )}
        {historyRows.length > 0 ? (
          <GuardrailHistory rows={historyRows} />
        ) : null}
        {tail}
      </Flex>
    );
  }

  const badge = panelBadge({
    decision,
    template,
    humanSteps,
    currentOrder,
    nowMs: actions.nowMs,
  });
  const headline = chainHeadline({
    decision,
    template,
    steps,
    currentOrder,
  });
  return (
    <Card role={announcementRole} data-testid="requisition-guardrail-outcome">
      <Flex flexDirection="column" gap="r">
        <GuardrailCardHeader badge={badge} />
        {headline ? (
          <P fontSize="2" m="0" weight="bold">
            {headline}
          </P>
        ) : null}
        {humanSteps.length > 0 ? (
          <ChainTimeline
            humanSteps={humanSteps}
            currentOrder={currentOrder}
            evaluation={evaluation}
            evalMarker={evalMarker}
            actions={actions}
            header={
              <Flex
                justifyContent="space-between"
                alignItems="center"
                gap="s"
                mb="s"
              >
                <Small uppercase weight="bold" color="subText">
                  Approval chain
                </Small>
                <Small color="subText">{progress}</Small>
              </Flex>
            }
          />
        ) : (
          evaluation
        )}
        {historyRows.length > 0 ? (
          <GuardrailHistory rows={historyRows} />
        ) : null}
        {tail}
      </Flex>
    </Card>
  );
};

const ChainOutcome = ({
  chain,
  layout,
  announcementRole,
  state,
  readOnly,
}: {
  chain: GuardrailChain;
  layout: ChainLayout;
  announcementRole: "alert" | "status";
  state?: string | null | undefined;
  readOnly: boolean;
}) => {
  const evaluation = chain.shown?.guardrailEvaluation;
  const needsRuleNames =
    (evaluation?.checks ?? []).some((check) => realRuleName(check) === "") ||
    (evaluation?.steps ?? []).some((step) =>
      (step.groups ?? []).some((group) =>
        (group.addingRules ?? []).some(
          (rule) =>
            realRuleName({ name: rule.ruleName, ruleId: rule.ruleId }) === ""
        )
      )
    );
  const labels = useGuardrailRuleLabels(needsRuleNames);
  if (chain.decision == null) {
    return null;
  }
  const shown = chain.shown;
  const historyRows = guardrailHistory(
    {
      openedDate: shown?.openedDate ?? null,
      createdAt: shown?.createdAt ?? null,
      createdBy: shown?.createdBy ?? null,
      guardrailEvaluation: shown?.guardrailEvaluation ?? null,
      guardrailDecisionLog: shown?.guardrailDecisionLog ?? null,
      lifecycleEvents: shown?.lifecycleEvents ?? null,
    },
    { rules: labels.rules, loaded: labels.loaded }
  );
  return (
    <RuleCatalogueContext.Provider
      value={{ rules: labels.rules, loaded: labels.loaded }}
    >
      <OutcomeCard
        layout={layout}
        announcementRole={announcementRole}
        decision={chain.decision}
        checks={chain.checks}
        stampSteps={chain.shown?.guardrailEvaluation?.steps}
        state={chain.shown?.state ?? state}
        template={chain.shown?.guardrailEvaluation?.template}
        evaluatedAt={chain.shown?.guardrailEvaluation?.evaluatedAt}
        actions={chain.actions}
        actionError={chain.actionError}
        historyRows={historyRows}
      />
      {!readOnly && chain.pendingReasonStep ? (
        <ExceptionReasonModal
          visible={chain.reasonModalOpen}
          step={chain.pendingReasonStep}
          draft={chain.reasonDraft}
          saving={chain.actions.saving}
          error={chain.reasonModalError}
          onChange={chain.onReasonDraft}
          onClose={chain.closeReasonModal}
          onSave={chain.actions.saveReason}
        />
      ) : null}
    </RuleCatalogueContext.Provider>
  );
};

/**
 * The chain without its own card, for a host card that already shows the
 * requisition. The host calls `useGuardrailChainActions` so it can lift
 * `requisitionChainBadge` into its header.
 */
export const RequisitionGuardrailSection = ({
  chain,
  state,
  readOnly = false,
}: {
  chain: GuardrailChain;
  state?: string | null | undefined;
  readOnly?: boolean;
}) => (
  <ChainOutcome
    chain={chain}
    layout="section"
    announcementRole="status"
    state={state}
    readOnly={readOnly}
  />
);

const RequisitionGuardrailOutcome = ({
  result,
  announcementRole = "alert",
  refusalReasons,
  readOnly = false,
}: {
  result?: GuardrailResult | null;
  announcementRole?: "alert" | "status";
  /** Story and test override. Production reads the workspace list. */
  refusalReasons?: RefusalReason[];
  /** Inbox deep link that no longer needs the caller's action — no writes. */
  readOnly?: boolean;
}) => {
  const chain = useGuardrailChainActions(result, readOnly, refusalReasons);
  return (
    <ChainOutcome
      chain={chain}
      layout="card"
      announcementRole={announcementRole}
      state={result?.state}
      readOnly={readOnly}
    />
  );
};

export default RequisitionGuardrailOutcome;
